// src/clock/detail.js — full-screen clock detail sheet (v88.0)
//
// Extracted from index.html in v88.0. Previously lived at lines 5757–5914
// in v87.1. Opens a full-screen panel for one staff member showing today's
// timeline, big-button actions, and last-known location.
//
// What's here:
//   - openClockDetail(staffId)  → reads window._clockCardData (cache populated
//                                  by renderSingleStoreClock) and paints the
//                                  detail panel + wires its action buttons
//   - closeClockDetail()        → hides the panel
//
// Cache contract: render.js populates window._clockCardData[id] with
// {staff, color, shift, event, isLate, isToday, schedText, hoursText,
//  punctClass, punctText} just before painting the card. openClockDetail
// reads that same key — they share the cache via window because both
// renderers need to populate it but detail is opened from outside the
// render closure (card.onclick, called any time after render returns).
//
// Free-variable dependencies (resolved from window at call time):
//   nowTime, requirePin, initials, escapeHtml, timeMins. All live as
//   inline globals in index.html.
//
// Inline onclicks NOT generated by this module: the close button in
// the static #clockDetail HTML (line ~961 in index.html). It's been
// rewritten to onclick="Clock.closeDetail()" as part of the v88.0
// extraction — see the index.html patch instructions.
//
// CATEGORY 1 — DELIBERATE PRODUCT RULES (do not "simplify"):
//
//   v80.8:  Break-clockout confirmation. When the user is on a break and
//           taps "End shift instead — HH:MM", the click handler shows a
//           confirm() dialog before letting the clock_out action proceed.
//           This was a real bug — Wafi City (5 May 2026) reported a staff
//           member who tapped Clock Out from the break state by accident.
//           The `data-cd-confirm="break-clockout"` attribute is the wire
//           between the markup and the gate logic. Don't remove the gate;
//           don't remove the secondary-button styling (it intentionally
//           makes End Shift NOT the prominent action).

import { } from './state.js';
// (No state imports needed today — detail reads ev from window._clockCardData
//  which is populated by render.js. Kept as an explicit no-op import so the
//  module boundary is documented if we ever need to add direct state reads.)
import { uiConfirm as sharedConfirm } from '../shared/confirm-dialog.js';

// v136.8 (accessibility audit, 16 Oct 2026): the two clock gates below used the browser's own confirm(), which is
// always English and says "OK" for everything. They now use the Shift dialog (src/shared/confirm-dialog.js), with the
// words passed through the translator. The answer still means the same: the action goes ahead only on the named button.
function tr(s) { return (typeof I18N !== 'undefined' && I18N.t) ? I18N.t(s) : s; }
function askConfirm(title, detail, opts) {
  const f = (typeof window !== 'undefined' && typeof window.uiConfirm === 'function') ? window.uiConfirm : sharedConfirm;
  return f(tr(title) + '\n\n' + detail.map(tr).join('\n\n'), opts);
}

export function openClockDetail(staffId){
  var d = (window._clockCardData||{})[staffId];
  if(!d) return;
  var s = d.staff, ev = d.event, sh = d.shift, color = d.color;
  var isToday = d.isToday;
  var nowT = nowTime();

  // Header
  document.getElementById('cdAvatar').style.background = color;
  document.getElementById('cdAvatar').textContent = initials(s.name);
  // v126.7 — the headshot replaces the initials when one exists (the Avatars
  // sweep hydrates any element with these attributes; fully guarded, display-only).
  try {
    var cdAv = document.getElementById('cdAvatar');
    cdAv.setAttribute('data-av-kind', 'staff');
    cdAv.setAttribute('data-av-id', s.id);
    cdAv.removeAttribute('data-av-done');
    cdAv.style.overflow = 'hidden';
  } catch (_e) {}
  document.getElementById('cdName').textContent = s.name;
  document.getElementById('cdSub').textContent = (s.role||'') + ' · ' + (s.store||'');

  // Body
  var body = document.getElementById('cdBody');
  var html = '';

  // Primary stat — depends on current status
  var statCls='stat-in', statNum='—', statLabel='Status';
  if(!isToday){
    statCls='stat-in'; statNum=d.hoursText||'—'; statLabel='Summary';
  } else if(ev.status==='in'){
    var liveMin = (new Date().getHours()*60 + new Date().getMinutes()) - timeMins(ev.clockIn||'00:00') - (ev.breakMins||0);
    if(liveMin<0)liveMin=0;
    var h=Math.floor(liveMin/60), m=liveMin%60;
    statCls='stat-in';
    statNum = h+'h '+(m<10?'0':'')+m+'m';
    statLabel='On floor since '+(ev.clockIn||'?');
  } else if(ev.status==='break'){
    var brkMin = (new Date().getHours()*60 + new Date().getMinutes()) - timeMins(ev.breakStart||'00:00');
    if(brkMin<0)brkMin=0;
    statCls='stat-break';
    statNum = brkMin + ' min';
    statLabel='On break since '+(ev.breakStart||'?');
  } else if(ev.clockIn&&ev.clockOut){
    var brkDed=(ev.breakMins!=null?ev.breakMins:(ev.breakDuration||60));
    var wMin=timeMins(ev.clockOut)-timeMins(ev.clockIn)-brkDed;
    statCls='stat-in';
    statNum = (wMin/60).toFixed(1)+'h';
    statLabel='Total worked today';
  } else if(d.isLate){
    statCls='stat-late';
    statNum = (sh&&sh.start)||'—';
    statLabel='Scheduled — not yet in';
  } else {
    statCls='stat-in';
    statNum = d.schedText.indexOf('–')>-1 ? d.schedText : '—';
    statLabel='Today\'s scheduled shift';
  }
  html += '<div class="cd-stat '+statCls+'">'+
    '<div class="cd-stat-num">'+escapeHtml(statNum)+'</div>'+
    '<div class="cd-stat-label">'+escapeHtml(statLabel)+'</div>'+
    (d.punctText ? '<div style="margin-top:8px"><span class="cc-chip '+d.punctClass+'">'+d.punctText+'</span></div>' : '')+
  '</div>';

  // Timeline — events recorded today
  var tlRows = [];
  if(ev.clockIn) tlRows.push({icon:'▶',cls:'tl-in',time:ev.clockIn,label:'Clocked in'});
  if(ev.breakMins && ev.breakMins>0){
    tlRows.push({icon:'⏸',cls:'tl-break',time:'',label:'Break taken — '+ev.breakMins+' min'});
  }
  if(ev.breakStart) tlRows.push({icon:'⏸',cls:'tl-break',time:ev.breakStart,label:'Break started'});
  if(ev.clockOut) tlRows.push({icon:'■',cls:'tl-out',time:ev.clockOut,label:'Clocked out'});

  if(tlRows.length){
    html += '<div class="cd-timeline">'+
      '<div class="cd-timeline-title">Today\'s timeline</div>';
    tlRows.forEach(function(r){
      html += '<div class="cd-tl-row">'+
        '<div class="cd-tl-icon '+r.cls+'">'+r.icon+'</div>'+
        (r.time ? '<div class="cd-tl-time">'+r.time+'</div>' : '')+
        '<div class="cd-tl-label">'+escapeHtml(r.label)+'</div>'+
      '</div>';
    });
    html += '</div>';
  }

  // Big action buttons — only on today
  if(isToday){
    html += '<div class="cd-actions">';
    if(ev.status==='in'){
      html += '<button class="cd-big-btn btn-primary-break" data-cd-act="break_start">⏸ Start Break — '+nowT+'</button>'+
              '<button class="cd-big-btn btn-primary-out" data-cd-act="clock_out">■ Clock Out — '+nowT+'</button>';
    } else if(ev.status==='break'){
      // v80.8: during a break, the user's expected next action is END BREAK,
      // not CLOCK OUT. Wafi City (5 May) reported a staff member who
      // accidentally clocked out instead of ending her break. Two visible
      // changes:
      //   1. End Break is the prominent green primary; Clock Out is now a
      //      smaller outline button that doesn't compete for attention.
      //   2. data-cd-confirm="break-clockout" tells the click handler to
      //      ask "Did you mean to end your break? You're still on break."
      //      before recording a clock_out from the break state.
      html += '<button class="cd-big-btn btn-primary-in" data-cd-act="break_end">▶ End Break — '+nowT+'</button>'+
              '<button class="cd-secondary-btn" data-cd-act="clock_out" data-cd-confirm="break-clockout" style="margin-top:8px;background:var(--card);color:var(--sub);border:1px solid var(--border);border-radius:10px;padding:10px 14px;font-size:13px;font-weight:600;width:100%;cursor:pointer;font-family:inherit">End shift instead — '+nowT+'</button>';
    } else if(ev.clockIn&&ev.clockOut){
      // v129.159 — SPLIT SHIFTS step 6 (spec #1177): a completed shift is no
      // longer a dead end. Staff on a split day come back for their second
      // leg (e.g. 09:00–13:00 home store, then an evening leg) and need to
      // clock IN again — doClock (action.js) starts session seq+1 when the
      // current session is closed; this button is the only way in from the
      // UI. It stays SECONDARY with a confirm gate so a routine end-of-day
      // tap can't accidentally open a second session.
      html += '<button class="cd-big-btn btn-disabled" disabled>Shift complete — see you tomorrow 👋</button>'+
              '<button class="cd-secondary-btn" data-cd-act="clock_in" data-cd-confirm="second-shift" style="margin-top:8px;background:var(--card);color:var(--sub);border:1px solid var(--border);border-radius:10px;padding:10px 14px;font-size:13px;font-weight:600;width:100%;cursor:pointer;font-family:inherit">▶ Start another shift — '+nowT+'</button>';
    } else {
      var lateStyle = d.isLate ? ' btn-primary-out' : ' btn-primary-in';
      var label = d.isLate ? '🔴 Clock In (Late) — ' : '▶ Clock In — ';
      html += '<button class="cd-big-btn'+lateStyle+'" data-cd-act="clock_in">'+label+nowT+'</button>';
    }
    html += '</div>';
  }

  // Location hint (if known from most recent event)
  if(ev.locationLabel || ev.locationDenied){
    if(ev.locationDenied){
      html += '<div class="cd-location loc-denied">🚫 Location permission denied on last clock action — flagged for review</div>';
    } else if(ev.locationLabel){
      html += '<div class="cd-location">📍 Last recorded at '+escapeHtml(ev.locationLabel)+'</div>';
    }
  }

  body.innerHTML = html;

  // Wire up big buttons to the existing action flow
  body.querySelectorAll('[data-cd-act]').forEach(function(btn){
    btn.addEventListener('click', async function(){
      var act = btn.getAttribute('data-cd-act');
      var confirmType = btn.getAttribute('data-cd-confirm');

      // v80.8 — confirm before clock_out from the break state. Wafi City
      // (5 May) reported a staff member who accidentally clocked out
      // when she meant to end her break. The break-clockout confirm gives
      // a clear "did you mean to end your shift?" gate before the
      // irreversible action. (v136.8: the Shift dialog, not confirm().)
      if (confirmType === 'break-clockout') {
        var ok = await askConfirm('End your shift now?', [
          "You're currently on a break.",
          'Tapping End Shift will record this as the END of your shift today, not the end of your break.',
          'If you just want to come back from your break, tap Cancel and choose "End Break" instead.'
        ], { danger: true, confirmLabel: tr('End Shift') });
        if (!ok) return;
      }

      // v129.159 — confirm before starting a SECOND session on a completed
      // day (split shifts, spec #1177). The first session stays recorded;
      // this gate stops "shift done" reflex-taps from opening session 2.
      if (confirmType === 'second-shift') {
        var ok2 = await askConfirm('Start your second shift now?', [
          'You already completed a shift today.',
          'Clocking in again starts a SECOND shift for today. Your first shift stays recorded exactly as it is.'
        ], { confirmLabel: tr('Start second shift') });
        if (!ok2) return;
      }

      closeClockDetail();
      requirePin(staffId, act, nowTime());
    });
  });

  document.getElementById('clockDetail').classList.add('open');
}

export function closeClockDetail(){
  document.getElementById('clockDetail').classList.remove('open');
}

// Click backdrop to close — attach once on module load. Same pattern as
// location.js (closeLocHelp click-outside listener).
(function(){
  var cd = document.getElementById('clockDetail');
  if(cd){
    cd.addEventListener('click', function(e){ if(e.target===cd) closeClockDetail(); });
  }
})();
